<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :disabled="isDisabled"
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
    open-on-click
  >

    <v-card data-cy="dialog-moveSelected-modal">
      <v-card-title
        class="d-flex justify-space-between"
        data-cy="dialog-moveSelected-title"
      >
        {{ $t('routes.node.moveDialogMoveSelected', itemsCount) }}

        <v-btn icon @click="dialog = false" :aria-label="$t('common.close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="scrollable mt-4">
        <div v-if="waitingForFtpCheck" class="text-center pt-5">
          <v-progress-circular class="float-left" indeterminate color="primary" :aria-label="$t('routes.node.loading')"></v-progress-circular>
          <h3 class="pl-2 mt-2 float-left">{{ $t('routes.node.loading') }}</h3>
        </div>

        <template v-if="!waitingForFtpCheck">
          <div v-if="affectedFtpUsers.length > 0" class="tw-warning" style="opacity: 0.65">
            <v-icon>mdi-alert-box</v-icon>
            <span>{{ $t('routes.node.moveFtpUsersWarning') }}</span>
            <ul class="mx-5 mt-1">
              <li v-for="group in affectedFtpUsersByNode" :key="group.nodeId">
                <router-link :to="'/node/' + group.nodeId">{{ group.nodeName }}</router-link>
                ({{ group.usernames.join(', ') }})
              </li>
            </ul>
          </div>

          <template v-else>
          <div v-if="itemsCount > 0 && targetNode">
            {{ $t('routes.node.moveDroppedIntoFolder', {name: targetNode.name}) }}

            <br />

            <!-- Nodes -->
            <v-list density="compact">
              <v-list-item v-for="node in getNodes" :key="node.id">
                <div>
                  {{ node.name }}
                </div>
                <template v-slot:append>
                  <v-btn
                    icon="mdi-close"
                    width="28px"
                    height="28px"
                    @click="removeNode(node)"
                    variant="text"
                    :aria-label="$t('routes.node.a11y.removeItem', { name: node.name })"
                  ></v-btn>
                </template>
              </v-list-item>

              <v-list-item v-for="pod in getPods" :key="pod.id">
                <div>
                  {{ pod.name }}
                </div>
                <template v-slot:append>
                  <v-btn
                    icon="mdi-close"
                    width="28px"
                    height="28px"
                    @click="removePod(pod)"
                    variant="text"
                    :aria-label="$t('routes.node.a11y.removeItem', { name: pod.name })"
                  ></v-btn>
                </template>
              </v-list-item>
            </v-list>
          </div>

          <div v-else>
            <div v-if="!targetNode">
              {{ $t('routes.node.moveDroppedNoDestination') }}
            </div>
            <div v-if="itemsCount === 0">
              {{ $t('routes.node.moveDroppedNoItemsSelected') }}
            </div>
          </div>

          <div v-if="hasArchived" class="bg-error my-2">
              {{ $t('routes.node.moveDroppedArchivedItemsCannotBeMoved') }}
          </div>

          <!-- Error message -->
          <div v-if="errorMessage && errorCode !== 'ERR233'" class="bg-error my-2" data-cy="dialog-moveDropped-errorMessage">
            <span>{{ errorMessage }}</span>
            <ul class="mx-5" v-if="conflictingNodeNames.length > 0">
              <li v-for="name in conflictingNodeNames" :key="name">
                {{ name }}
              </li>
            </ul>
          </div>

          <!-- Depth exceeded error (ERR233) -->
          <div v-if="errorCode === 'ERR233' && errorItems" class="bg-error my-2">
            {{ $t('backend.ERR233', errorParams) }}
            <span v-if="errorItems.length === 1">
              {{ $t('routes.node.depthExceededTooDeep', errorItems[0].children.length) }}
              <DepthExceededChildren :children="errorItems[0].children" @navigate="dialog = false" />
            </span>
            <template v-else>
              {{ $t('routes.node.depthExceededTooDeep', 2) }}
              <ul class="mx-5 mt-1">
                <li v-for="item in errorItems" :key="item.parent.id">
                  <strong>{{ item.parent.name }}</strong> &gt;
                  <DepthExceededChildren :children="item.children" @navigate="dialog = false" />
                </li>
              </ul>
            </template>
          </div>

          <!-- Success message -->
          <div v-if="successMessage" class="bg-success" data-cy="dialog-moveDropped-successMessage">
            {{successMessage}}
          </div>

          <!-- Info messages (validation warnings) -->
          <div v-for="infoMsg in infoMessages" :key="infoMsg" class="bg-info my-2">
            {{ infoMsg }}
          </div>
        </template>
        </template>
      </v-card-text>

      <v-card-actions>
        <v-btn
          v-if="showingUndoButton && affectedFtpUsers.length === 0"
          color="primary"
          variant="flat"
          elevation="0"
          :disabled="submitDisabled"
          @click="submit"
          data-cy="dialog-moveDropped-confirm-btn"
        >
          <span v-if="!showingOKtoConfirm">{{ $t('common.undo') }}</span>
          <span v-else>{{ $t('common.ok') }}</span>
        </v-btn>

        <v-spacer></v-spacer>

        <!-- Confirm button (changes to OK when validation warnings exist) -->
        <v-btn
          v-if="!showingUndoButton && affectedFtpUsers.length === 0"
          color="primary"
          variant="flat"
          elevation="0"
          :disabled="submitDisabled"
          @click="submit"
          data-cy="dialog-moveDropped-confirm-btn"
        >
          <span v-if="!showingOKtoConfirm">{{ $t('common.move') }}</span>
          <span v-else>{{ $t('common.ok') }}</span>
        </v-btn>

        <v-btn @click="dialog = false" variant="text" data-cy="dialog-moveDropped-cancel-btn">
          {{ $t('common.close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { usePodStore } from '@/stores/PodStore.js';
import { useNodeStore } from '@/stores/NodeStore.js';
import { useAuthStore } from '@/stores/AuthStore.js';
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';
import DialogModel from '@/components/mixin/DialogModel.js';
import DepthExceededChildren from '@/components/DepthExceededChildren.vue';

export default {
  name: 'MoveDroppedDialog',
  mixins: [DialogActionKeys, DialogModel],
  components: { DepthExceededChildren },
  props: {
    isDisabled: {
      type: Boolean,
      required: false,
    },
    targetNodeId: {
      type: String,
      required: false,
    },
    podIds: {
      type: Array,
      default: () => [],
    },
    nodeIds: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      conflictingNodeNames: [],
      errorCode: null,
      errorParams: {},
      errorItems: null,
      skippedNodes: [],
      skippedPods: [],
      errorMessage: '',
      successMessage: '',
      infoMessages: [],
      preventDoubleClick: false,
      showingOKtoConfirm: false,

      showingUndoButton: false,
      sourceNodeId: null,

      waitingForFtpCheck: false,
      affectedFtpUsers: [],
    };
  },
  watch: {
    async dialog(newVal) {
      if (newVal) {
        this.resetForm();

        if (!this.podIds && !this.nodeIds) {
          this.errorMessage = this.$t('routes.node.moveDroppedInvalidItemsSelected');
        }

        if (this.nodeIds && this.nodeIds.length > 0) {
          this.waitingForFtpCheck = true;
          const nodeStore = useNodeStore();
          try {
            const ftpUsers = await nodeStore.getAffectedFtpUsers(this.nodeIds);
            this.affectedFtpUsers = ftpUsers || [];
          } catch (error) {
            console.log('Error checking affected FTP users:', error);
            this.affectedFtpUsers = [];
          } finally {
            this.waitingForFtpCheck = false;
          }
        }
      }
    },
  },
  methods: {
    resetForm() {
      // Reset everything when dialog closes
      this. conflictingNodeNames = [];
      this.errorCode = null;
      this.errorParams = {};
      this.errorItems = null;
      this.skippedNodes = [];
      this.skippedPods = [];
      this.errorMessage = '';
      this.successMessage = '';
      this.infoMessages = [];
      this.showingOKtoConfirm = false;
      this.preventDoubleClick = false;

      this.showingUndoButton = false;
      this.sourceNodeId = this.getNodes?.[0]?.parentid ?? this.getPods?.[0]?.nodeid;

      this.affectedFtpUsers = [];
      this.waitingForFtpCheck = false;
    },
    removeNode(node) {
      this.skippedNodes.push(node.id);
    },
    removePod(pod) {
      this.skippedPods.push(pod.id);
    },

    async submit() {
      this.successMessage = '';
      if (this.showingOKtoConfirm) {
        await this.submitNoValidate();
        return;
      }
      await this.submitValidate();
    },

    async submitValidate() {
      this.errorMessage = '';
      this.infoMessages = [];
      this.preventDoubleClick = true;

      if (!this.targetNode || this.itemsCount === 0) {
        this.preventDoubleClick = false;
        return;
      }

      // If only pods → move directly (pods have no validation step)
      if (this.getNodes.length === 0) {
        await this.submitNoValidate();
        return;
      }

      // Nodes need validation first
      const nodeStore = useNodeStore();
      // const currentNode = nodeStore.getCurrentNode;

      const authStore = useAuthStore();
      const currentAccessPoint = authStore.getActiveAccesspoint ? authStore.getActiveAccesspoint.node : null;

      if (!currentAccessPoint) {
        return;
      }

      const payload = {
        targetnodeid: this.targetNode.id,
        nodeid: currentAccessPoint?.id || null,
        nodes: this.getNodes.map((n) => n.id),
        archive: this.targetNode.archived === 1 ? 1 : 0,
        validateRequestOnly: 1,
      };

      try {
        const response = await nodeStore.moveManyNodes(payload);

        if (response?.validation === true) {
          const messages = response.messages?.flat() || [];
          if (messages.length > 0) {
            this.infoMessages = messages.map((msg) =>
              this.$localizeMessage(msg)
            );
            this.showingOKtoConfirm = true;
          } else {
            await this.submitNoValidate();
          }
        }
        this.preventDoubleClick = false;
      } catch (error) {
        const err = error?.response?.data?.error;
        if (err?.code === 'ERR233') {
          this.errorCode = err.code;
          this.errorParams = { ...err.params };
          if (err.extra?.items) {
            this.errorItems = err.extra.items;
          }
        } else {
          this.errorMessage = this.$localizeMessage(err, this.$t('backend.system-error'));
        }
        this.preventDoubleClick = false;
      }
    },

    async submitNoValidate() {
      this.errorMessage = '';
      this.infoMessages = [];
      this.preventDoubleClick = true;

      if (!this.targetNode || this.itemsCount === 0) {
        this.preventDoubleClick = false;
        return;
      }

      const promises = [];

      // Move nodes
      if (this.getNodes.length > 0) {
        const nodeStore = useNodeStore();
        const currentNode = nodeStore.getCurrentNode;

        const payload = {
          targetnodeid: this.targetNode.id,
          nodeid: currentNode?.id || null,
          nodes: this.getNodes.map((n) => n.id),
          archive: this.targetNode.archived === 1 ? 1 : 0,
          validateRequestOnly: 0,
        };
        promises.push(nodeStore.moveManyNodes(payload));
      }

      // Move pods
      if (this.getPods.length > 0) {
        const podStore = usePodStore();
        const currentNode = useNodeStore().getCurrentNode;

        const payload = {
          targetnodeid: this.targetNode.id,
          nodeid: currentNode?.id || null,
          pods: this.getPods.map((p) => p.id),
          archive: this.targetNode.archived === 1 ? 1 : 0,
        };
        promises.push(podStore.moveManyPods(payload));
      }

      try {
        await Promise.all(promises);

        // Deselect everything and close
        usePodStore().deselectAllPods();
        useNodeStore().deselectAllNodes();
        // this.dialog = false;
        //
        this.showingUndoButton = !this.showingUndoButton;
        this.successMessage = this.$t('routes.node.moveDroppedSuccessfullyMoved');
      } catch (error) {
        const err = error?.response?.data?.error;
        if (err?.code === 'ERR233') {
          this.errorCode = err.code;
          this.errorParams = { ...err.params };
          if (err.extra?.items) {
            this.errorItems = err.extra.items;
          }
        } else {
          this.errorMessage = this.$localizeMessage(err, this.$t('backend.system-error'));
        }
      } finally {
        this.preventDoubleClick = false;
      }
    },
  },
  computed: {
    itemsCount() {
      return this.getNodes.length + this.getPods.length;
    },
    getNodes() {
      const nodeStore = useNodeStore();
      return this.nodeIds
        .filter((nodeId) => !this.skippedNodes.includes(nodeId))
        .map((nodeId) => nodeStore.getNodeById(nodeId))
        .filter(Boolean) || [];
    },
    getPods() {
      const podStore = usePodStore();
      return this.podIds
        .filter((podId) => !this.skippedPods.includes(podId))
        .map((podId) => podStore.getPodById(podId))
        .filter(Boolean) || [];
    },
    hasArchived() {
      return this.getNodes.some(x => x.archived) || this.getPods.some(x => x.archived);
    },
    targetNode() {
      const nodeStore = useNodeStore();

      // Change the target node to original source node
      // if we press the undo button
      if (this.showingUndoButton && this.sourceNodeId) {
        return nodeStore.getNodeById(this.sourceNodeId);
      }

      if (this.targetNodeId) {
        return nodeStore.getNodeById(this.targetNodeId);
      }

      return null;
    },
    submitDisabled() {
      return (
        !this.targetNode ||
        this.itemsCount === 0 ||
        this.hasArchived ||
        this.preventDoubleClick ||
        this.waitingForFtpCheck ||
        this.affectedFtpUsers.length > 0
      );
    },
    affectedFtpUsersByNode() {
      const groups = {};
      for (const user of this.affectedFtpUsers) {
        if (!groups[user.nodeId]) {
          groups[user.nodeId] = { nodeId: user.nodeId, nodeName: user.nodeName, usernames: [] };
        }
        groups[user.nodeId].usernames.push(user.username);
      }
      return Object.values(groups);
    },
  },
};
</script>

<style scoped></style>
